import { Card, Table } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { DetailItem } from 'components/details/DetailItem';
import { DetailList } from 'components/details/DetailList';
import { EditString } from 'components/forms/EditString';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import type { EditableHandle } from '../../edit/EditableHandle';

import './Handles.scss';

// A conditions node's handles carry a `condition` expression; every other type carries `return_codes`. The node type is fixed
// for the whole edit, so this is decided once per render, not per handle.
const groupLabel = (isConditions: boolean) => (isConditions ? 'Condition Handles' : 'Handles');

// Its own observer: DetailList renders each item inside a drag render-prop (outside its own reaction), so the field reads must
// be tracked here for edits to re-render the row. The Label and condition/return-codes controls share the DetailItem content
// row and stretch evenly.
const HandleRow = observer(
  ({
    handle,
    isConditions,
    index,
    total,
    dragHandle,
    reorder,
  }: {
    readonly handle: EditableHandle;
    readonly isConditions: boolean;
    readonly index: number;
    readonly total: number;
    readonly dragHandle: React.ReactNode;
    readonly reorder: (fromIndex: number, toIndex: number) => void;
  }) => (
    <DetailItem
      index={index + 1}
      dragHandle={dragHandle}
      onMoveUp={index > 0 ? () => reorder(index, index - 1) : undefined}
      onMoveDown={index < total - 1 ? () => reorder(index, index + 1) : undefined}
      onRemove={handle.delete}
      removeLabel="Remove handle"
    >
      <div className="flex-fill">
        <EditString label="Label" value={handle.label} onChange={(v) => (handle.label = v)} />
      </div>
      <div className="flex-fill">
        {isConditions ? (
          <EditString label="Condition" value={handle.condition} onChange={(v) => (handle.condition = v)} />
        ) : (
          <EditString label="Return Codes" value={handle.returnCodes} onChange={(v) => (handle.returnCodes = v)} />
        )}
      </div>
    </DetailItem>
  )
);

// Editable path: an ordered, drag-reorderable DetailList over the definition's `handles` Aggregate (add / remove / reorder /
// edit). Order matters (conditions are first-match), so the list is reorderable.
const EditableHandles = observer(({ entity }: { readonly entity: EditableNodeDefinition }) => {
  const isConditions = entity.type === 'conditions';
  const handles = entity.handles;
  return (
    <div className="mb-2">
      <h5 className="mb-2">{groupLabel(isConditions)}</h5>
      <DetailList
        items={handles}
        getKey={(handle) => handle.id}
        onReorder={entity.reorderHandles}
        onAdd={entity.createHandle}
        addLabel="Add handle"
        renderItem={(handle, index, dragHandle) => (
          <HandleRow handle={handle} isConditions={isConditions} index={index} total={handles.length} dragHandle={dragHandle} reorder={entity.reorderHandles} />
        )}
      />
    </div>
  );
});

// Read-only path (node types that own their handles): a small headed table. Not DetailList-based — nothing to add, remove,
// reorder or disable — and a table with a value-column header ("Condition" / "Return Codes") makes an empty value legible
// where a bare definition list couldn't.
const ReadOnlyHandles = observer(({ entity }: { readonly entity: EditableNodeDefinition }) => {
  const isConditions = entity.type === 'conditions';
  const handles = entity.handles;
  if (handles.length === 0) return null;
  return (
    <div className="mb-2">
      <h5 className="mb-2">{groupLabel(isConditions)}</h5>
      <Card className="handles-readonly-card">
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Label</th>
              <th>{isConditions ? 'Condition' : 'Return Codes'}</th>
            </tr>
          </thead>
          <tbody>
            {handles.map((handle) => (
              <tr key={handle.id}>
                <td>{handle.label || <span className="text-body-tertiary">(no label)</span>}</td>
                <td>{(isConditions ? handle.condition : handle.returnCodes) || <span className="text-body-tertiary">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
});

/**
 * Node output handles. Splits on the node type's `editableHandles` capability (a stable flag decided at load, never toggled
 * mid-edit): editable types get a reorderable add/remove list, fixed-handle types get a clean read-only view. Reused by both
 * the Properties tab and the `conditions` type's Definition-tab editor, over the same `handles` Aggregate — so the two stay in
 * sync automatically.
 */
const Handles = observer(({ entity }: { readonly entity: EditableNodeDefinition }) =>
  entity.editableHandles ? <EditableHandles entity={entity} /> : <ReadOnlyHandles entity={entity} />
);

export default Handles;
